/**
 * Say how a task command ended, whatever happened to the control that started
 * it (spec 151 FR-001).
 *
 * Takes the promise `mutateAsync` returns, which settles whether or not the
 * calling component is still mounted; callbacks handed to `mutate` are
 * dropped once it unmounts, which an optimistic move always causes. Exactly
 * one of `accepted` and `failed` runs.
 *
 * Anything thrown while saying the outcome (a toast, a focus hand-off) is
 * reported, never swallowed: the command itself has already ended, so it is
 * not turned into a second, wrong "failed" message.
 */
export function sayOutcome<T>(
  pending: Promise<T>,
  handlers: { accepted: (value: T) => void; failed: (error: unknown) => void },
): void {
  pending.then(handlers.accepted, handlers.failed).catch((error: unknown) => {
    console.warn('task command: saying its outcome failed', error);
  });
}

/**
 * How a task command ended, for a caller that keeps the words: the Overview
 * leaves a refused action's reason on its row (spec 154 R2). Resolved after
 * the command's own toast, never rejected.
 */
export type CommandResult = { ok: true } | { ok: false; message: string };

/** How a command is sent. `keepalive` outlives the page (spec 154 R3). */
export interface CommandOptions {
  keepalive?: boolean;
}

/** A rejection's words. Mutations reject with `Error`; anything else is said as itself. */
export function messageOf(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}
